import React, { createContext, useContext, useReducer, useCallback, useEffect } from 'react';
import AxiosService from '../lib/axios';
import { getUserId } from '../lib/Router';
import logger from '../utils/logger';

// Estado inicial
const initialState = {
    notifications: [],
    role: "",
    permissions: [],
    roles: [],
    vrole: {},
    users: [],
    vuser: {},
    ecosystem_users: [],
    quizzes: [],
    vquizz: {},
    campaings: [],
    vcampaing: {}
};

// Tipos de acciones
const ActionTypes = {
    UPDATE_STATE: 'UPDATE_STATE',
    ADD_NOTIFICATION: 'ADD_NOTIFICATION',
    REMOVE_NOTIFICATION: 'REMOVE_NOTIFICATION',
    SET_ROLE: 'SET_ROLE',
    SET_PERMISSIONS: 'SET_PERMISSIONS',
    SET_ROLES: 'SET_ROLES',
    SET_VROLE: 'SET_VROLE',
    SET_USERS: 'SET_USERS',
    SET_VUSER: 'SET_VUSER',
    SET_ECOSYSTEM_USERS: 'SET_ECOSYSTEM_USERS',
    SET_QUIZZES: 'SET_QUIZZES',
    SET_VQUIZZ: 'SET_VQUIZZ',
    SET_CAMPAINGS: 'SET_CAMPAINGS',
    SET_VCAMPAING: 'SET_VCAMPAING',
};

// Reducer
const appReducer = (state, action) => {
    switch (action.type) {
        case ActionTypes.UPDATE_STATE:
            return { ...state, ...action.payload };
        
        case ActionTypes.ADD_NOTIFICATION:
            return {
                ...state,
                notifications: [...state.notifications, action.payload]
            };
        
        case ActionTypes.REMOVE_NOTIFICATION:
            return {
                ...state,
                notifications: state.notifications.filter(n => n.id !== action.payload.id)
            };
        
        case ActionTypes.SET_ROLE:
            return { ...state, role: action.payload };
        
        case ActionTypes.SET_PERMISSIONS:
            return { ...state, permissions: action.payload };
        
        case ActionTypes.SET_ROLES:
            return { ...state, roles: action.payload };
        
        case ActionTypes.SET_VROLE:
            return { ...state, vrole: action.payload };
        
        case ActionTypes.SET_USERS:
            return { ...state, users: action.payload };
        
        case ActionTypes.SET_VUSER:
            return { ...state, vuser: action.payload };
        
        case ActionTypes.SET_ECOSYSTEM_USERS:
            return { ...state, ecosystem_users: action.payload };
        
        case ActionTypes.SET_QUIZZES:
            return { ...state, quizzes: action.payload };
        
        case ActionTypes.SET_VQUIZZ:
            return { ...state, vquizz: action.payload };
        
        case ActionTypes.SET_CAMPAINGS:
            return { ...state, campaings: action.payload };
        
        case ActionTypes.SET_VCAMPAING:
            return { ...state, vcampaing: action.payload };
        
        default:
            return state;
    }
};

// Crear Context
const AppContext = createContext(null);

// Provider Component
export const AppProvider = ({ children }) => {
    const [state, dispatch] = useReducer(appReducer, initialState);

    // Función para actualizar estado (similar a updateState de Ngex)
    const updateState = useCallback((updater) => {
        if (typeof updater === 'function') {
            const updatedState = updater(state);
            if (typeof updatedState === 'object' && updatedState !== null) {
                dispatch({ type: ActionTypes.UPDATE_STATE, payload: updatedState });
            }
        } else {
            console.error('Updater debe ser una función que retorna el nuevo estado');
        }
    }, [state]);

    // Función para obtener estado
    const getState = useCallback(() => state, [state]);

    // Función para notificaciones
    const notify = useCallback((payload = {}) => {
        const noty = {
            id: payload.id !== undefined ? parseInt(payload.id) : Math.random().toString(36).slice(-8),
            control: payload.id !== undefined ? "custom" : "default",
            type: payload.type !== undefined ? payload.type : 'info',
            title: payload.title !== undefined ? payload.title : 'Información',
            text: payload.text !== undefined ? payload.text : '',
            time: payload.time !== undefined ? parseInt(payload.time) : 2000,
        };

        dispatch({ type: ActionTypes.ADD_NOTIFICATION, payload: noty });
        return noty.id;
    }, []);

    // Función para remover notificación
    const removeNotification = useCallback((payload) => {
        dispatch({ type: ActionTypes.REMOVE_NOTIFICATION, payload });
    }, []);

    // Función para parsear datos por select
    const parseDataBySelect = useCallback((object, label) => {
        return {
            ...object,
            label: label,
            value: object._id
        };
    }, []);

    // Función para cargar datos principales
    const loadMain = useCallback(async () => {
        try {
            const { permissions, role } = await AxiosService.getPermissionRole();
            dispatch({ type: ActionTypes.SET_ROLE, payload: role });
            dispatch({ type: ActionTypes.SET_PERMISSIONS, payload: permissions });
        } catch (error) {
            logger.error('Error al cargar datos principales:', error);
        }
    }, []);

    // Función para obtener permisos
    const getPermissions = useCallback(async () => {
        return await AxiosService.getPermissionRole();
    }, []);

    // Funciones para localStorage
    const getLS = useCallback((key) => {
        return localStorage.getItem(key);
    }, []);

    const setLS = useCallback((key, value) => {
        return localStorage.setItem(key, value);
    }, []);

    // Obtener profile (user ID)
    const profile = getUserId();

    // Cargar datos principales al montar
    useEffect(() => {
        loadMain();
    }, [loadMain]);

    const value = {
        // Estado
        state,
        getState,
        
        // Acciones
        updateState,
        dispatch,
        
        // Notificaciones
        notify,
        removeNotification,
        
        // Utilidades
        parseDataBySelect,
        loadMain,
        getPermissions,
        getLS,
        setLS,
        profile,
        
        // Constantes
        kId: "itfgscomm",
        idwlKey: "lwfgscomm",
    };

    return (
        <AppContext.Provider value={value}>
            {children}
        </AppContext.Provider>
    );
};

// Hook para usar el contexto
export const useApp = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useApp debe ser usado dentro de AppProvider');
    }
    return context;
};

// Hook para suscribirse a cambios de una propiedad específica (similar a Applications.on)
// Este hook causa re-render cuando cambia la propiedad
export const useAppState = (propertyName) => {
    const { state } = useApp();
    const [value, setValue] = React.useState(state[propertyName]);

    React.useEffect(() => {
        setValue(state[propertyName]);
    }, [state, propertyName]);

    return value;
};

// Hook para notificaciones
export const useNotifications = () => {
    const { notify, removeNotification, state } = useApp();
    return {
        notifications: state.notifications,
        notify,
        removeNotification,
    };
};

// Hook para permisos
export const usePermissions = () => {
    const { state, getPermissions, loadMain } = useApp();
    return {
        permissions: state.permissions,
        role: state.role,
        getPermissions,
        loadMain,
    };
};

export default AppContext;

